// Helper murni Web Push (PWA Tahap 2 - Push Notifications). Tanpa Supabase/DOM
// kompleks agar mudah diuji. Kunci publik VAPID di-inline saat build dari env;
// sejak 2026-07 juga bisa dari `site_settings` (runtime — lihat supabase/push.ts)
// karena env NEXT_PUBLIC_* hilang saat build pindah host (kasus Hostinger).

/** Kunci publik VAPID dari build (aman dipublikasikan). Set NEXT_PUBLIC_VAPID_PUBLIC_KEY di env. */
export const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY ?? "";

/** Rapikan kunci VAPID hasil tempel admin: buang spasi/baris baru & tanda kutip. */
export function normalizeVapidPublicKey(raw: string | null | undefined): string {
  return (raw ?? "").replace(/["']/g, "").replace(/\s+/g, "");
}

/**
 * Validasi kunci publik VAPID (input admin). Kembalikan pesan error (string)
 * bila tidak valid, atau `null` bila valid: base64url yang terdekode menjadi
 * 65 byte titik publik P-256 tak-terkompresi (byte pertama 0x04).
 */
export function validateVapidPublicKey(raw: string): string | null {
  const key = normalizeVapidPublicKey(raw);
  if (!key) return "Kunci publik VAPID belum diisi.";
  if (!/^[A-Za-z0-9_-]+$/.test(key))
    return "Kunci mengandung karakter tidak valid (harus base64url).";
  let bytes: Uint8Array;
  try {
    bytes = urlBase64ToUint8Array(key);
  } catch {
    return "Kunci tidak bisa didekode (bukan base64url yang benar).";
  }
  if (bytes.length !== 65 || bytes[0] !== 0x04)
    return "Bukan kunci publik VAPID yang benar (harus 65 byte P-256; biasanya 87 karakter diawali \"B\").";
  return null;
}

/**
 * Bandingkan `applicationServerKey` langganan browser yang ada dengan kunci
 * VAPID terkonfigurasi. `true`/`false` = bisa dibandingkan; `null` = kunci
 * langganan tak terbaca (browser lama) — anggap sama (perilaku lama).
 */
export function sameApplicationServerKey(
  existing: ArrayBuffer | ArrayBufferView | null | undefined,
  vapidKey: string
): boolean | null {
  if (!existing) return null;
  const a =
    existing instanceof ArrayBuffer
      ? new Uint8Array(existing)
      : new Uint8Array(existing.buffer, existing.byteOffset, existing.byteLength);
  let b: Uint8Array;
  try {
    b = urlBase64ToUint8Array(normalizeVapidPublicKey(vapidKey));
  } catch {
    return null;
  }
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Ubah kunci VAPID base64url menjadi Uint8Array untuk `applicationServerKey`.
 * (PushManager.subscribe tidak menerima string base64url langsung.)
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/** True bila browser mendukung service worker + Push API + Notification. */
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// ── Opt-out per-jenis (penyaringan push sisi server, mig 152) ────────────────
// Preferensi tampilan pembeli (localStorage, model opt-out: hanya jenis yang
// DINONAKTIFKAN yang ber-nilai `false`) dicermin ke baris langganan agar Edge
// Function bisa melewati pengiriman push untuk jenis itu.

/** Daftar jenis yang dinonaktifkan dari objek prefs (nilai `false`). */
export function mutedTypesFromPrefs(prefs: Record<string, boolean>): string[] {
  return Object.entries(prefs)
    .filter(([, enabled]) => enabled === false)
    .map(([type]) => type)
    .sort();
}

/**
 * Apakah push untuk notifikasi ini boleh dikirim ke suatu langganan.
 * Cermin TS dari penyaringan di Edge Function `push-send`: notif "penting"
 * (broadcast kritis) selalu lolos; selain itu di-blok bila jenisnya di-mute.
 */
export function shouldSendPush(
  notif: { type?: string | null; important?: boolean | null },
  mutedTypes: readonly string[] | null | undefined
): boolean {
  if (notif.important === true) return true;
  const type = notif.type;
  if (!type || !mutedTypes || mutedTypes.length === 0) return true;
  return !mutedTypes.includes(type);
}
